import { useCallback, useMemo, useState } from 'react';
import { type UserType } from '../UserTypeSelector';
import type { LinkedUser } from '../../types';

export interface EmployeeFormData {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    email: string;
    roleId: string;
    isMainContact: boolean;
    userType: UserType;
    userEmail: string;
    userEmailIsEmail: boolean;
    linkedUser: LinkedUser | null;
}

export type EmployeeFormDataType = EmployeeFormData;

export function createInitialFormData(
    initialEmail?: string,
    initialUserName?: string,
    linkedUser?: LinkedUser | null
): EmployeeFormDataType {
    const email = linkedUser?.email || initialEmail || '';

    return {
        firstName: linkedUser?.username || initialUserName || '',
        lastName: '',
        phoneNumber: '',
        email,
        roleId: '',
        isMainContact: false,
        userType: 'default',
        userEmail: email,
        userEmailIsEmail: true,
        linkedUser: linkedUser ?? null,
    };
}

export function useEmployeeFormValidation(formData: EmployeeFormData): boolean {
    return useMemo((): boolean => {
        if (!formData.firstName.trim()) return false;
        if (!formData.lastName.trim()) return false;
        if (!formData.email.trim()) return false;
        if (!formData.roleId) return false;
        if (!formData.userType) return false;
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return false;
        // A linked user account replaces the invite email, so it is not validated
        if (formData.linkedUser) return true;
        if (!formData.userEmail.trim()) return false;
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.userEmail)) return false;
        return true;
    }, [formData]);
}

export function useEmployeeFormHandlers(
    initialEmail?: string,
    initialUserName?: string,
    initialLinkedUser?: LinkedUser | null
) {
    const [formData, setFormData] = useState<EmployeeFormDataType>(
        createInitialFormData(initialEmail, initialUserName, initialLinkedUser)
    );
    const [errors, setErrors] = useState<Partial<Record<keyof EmployeeFormDataType, string>> & { general?: string }>({});

    const handleChange = useCallback(
        (field: keyof EmployeeFormDataType) =>
            (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
                const value =
                    e.target.type === 'checkbox'
                        ? (e.target as HTMLInputElement).checked
                        : e.target.value;

                setFormData((prev) => {
                    let userEmailIsEmail = prev.userEmailIsEmail;
                    let userEmail = prev.userEmail;
                    if (field === 'userEmail') {
                        userEmailIsEmail = false;
                        userEmail = value as string;
                    } else if (field === 'email' && userEmailIsEmail) {
                        userEmail = value as string;
                    }
                    return { ...prev, [field]: value, userEmailIsEmail, userEmail };
                });

                setErrors((prev) => ({ ...prev, [field]: undefined }));
            },
        []
    );

    const handleToggleMainContact = useCallback(() => {
        setFormData((prev) => ({ ...prev, isMainContact: !prev.isMainContact }));
    }, []);

    const handleSyncEmails = useCallback(() => {
        setFormData((prev) => ({
            ...prev,
            userEmail: prev.email,
            userEmailIsEmail: true,
        }));
        setErrors((prev) => ({ ...prev, userEmail: undefined }));
    }, []);

    // Link an existing user account and prefill the contact fields from it.
    // A field is filled when it is empty or still holds the previously linked user's value.
    const handleSelectUser = useCallback((linkedUser: LinkedUser) => {
        setFormData((prev) => {
            const isFirstNameFromPreviousUser = prev.firstName === prev.linkedUser?.username;
            const isEmailFromPreviousUser = prev.email === prev.linkedUser?.email;

            return {
                ...prev,
                linkedUser,
                firstName:
                    !prev.firstName || isFirstNameFromPreviousUser
                        ? linkedUser.username
                        : prev.firstName,
                email: !prev.email || isEmailFromPreviousUser ? linkedUser.email : prev.email,
            };
        });
        setErrors((prev) => ({ ...prev, email: undefined, userEmail: undefined, general: undefined }));
    }, []);

    const handleClearUser = useCallback(() => {
        setFormData((prev) => ({ ...prev, linkedUser: null }));
    }, []);

    const isFormValid = useEmployeeFormValidation(formData);

    return {
        formData,
        setFormData,
        errors,
        setErrors,
        handleChange,
        handleToggleMainContact,
        handleSyncEmails,
        handleSelectUser,
        handleClearUser,
        isFormValid,
    };
}
